import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { DEMO_DATA } from '@/lib/demo-fixtures';

interface DemoContextValue {
  isDemoMode: boolean;
  /** True when demo mode was auto-enabled (e.g. after onboarding) rather than manually toggled. */
  isAutoDemo: boolean;
  enableDemo: () => void;
  disableDemo: () => void;
  toggleDemo: () => void;
  /** Dismiss the auto-demo banner without disabling demo mode itself. */
  dismissAutoDemoBanner: () => void;
  /** Whether the auto-demo banner should be visible. */
  showAutoDemoBanner: boolean;
  demoData: typeof DEMO_DATA;
}

const DemoContext = createContext<DemoContextValue | null>(null);

const DEMO_STORAGE_KEY = 'lumenous-demo-mode';
const AUTO_DEMO_KEY = 'lumenous-auto-demo';
const AUTO_DEMO_BANNER_DISMISSED_KEY = 'lumenous-auto-demo-banner-dismissed';

export function DemoProvider({ children }: { children: ReactNode }) {
  const [isDemoMode, setIsDemoMode] = useState(() => {
    try {
      return localStorage.getItem(DEMO_STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [isAutoDemo, setIsAutoDemo] = useState(() => {
    try {
      return localStorage.getItem(AUTO_DEMO_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try {
      return localStorage.getItem(AUTO_DEMO_BANNER_DISMISSED_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DEMO_STORAGE_KEY, String(isDemoMode));
    } catch {
      // localStorage unavailable
    }
  }, [isDemoMode]);

  useEffect(() => {
    try {
      localStorage.setItem(AUTO_DEMO_KEY, String(isAutoDemo));
    } catch {
      // localStorage unavailable
    }
  }, [isAutoDemo]);

  const enableDemo = useCallback(() => {
    setIsDemoMode(true);
    setIsAutoDemo(true);
    setBannerDismissed(false);
    try { localStorage.removeItem(AUTO_DEMO_BANNER_DISMISSED_KEY); } catch { /* noop */ }
  }, []);

  const disableDemo = useCallback(() => {
    setIsDemoMode(false);
    setIsAutoDemo(false);
    setBannerDismissed(false);
    try {
      localStorage.removeItem(AUTO_DEMO_KEY);
      localStorage.removeItem(AUTO_DEMO_BANNER_DISMISSED_KEY);
    } catch { /* noop */ }
  }, []);

  const toggleDemo = useCallback(() => {
    setIsDemoMode(prev => {
      const next = !prev;
      if (!next) {
        setIsAutoDemo(false);
        try {
          localStorage.removeItem(AUTO_DEMO_KEY);
          localStorage.removeItem(AUTO_DEMO_BANNER_DISMISSED_KEY);
        } catch { /* noop */ }
      }
      return next;
    });
  }, []);

  const dismissAutoDemoBanner = useCallback(() => {
    setBannerDismissed(true);
    try { localStorage.setItem(AUTO_DEMO_BANNER_DISMISSED_KEY, 'true'); } catch { /* noop */ }
  }, []);

  const showAutoDemoBanner = isDemoMode && isAutoDemo && !bannerDismissed;

  return (
    <DemoContext.Provider value={{
      isDemoMode, isAutoDemo, enableDemo, disableDemo, toggleDemo,
      dismissAutoDemoBanner, showAutoDemoBanner, demoData: DEMO_DATA,
    }}>
      {children}
    </DemoContext.Provider>
  );
}

export function useDemo(): DemoContextValue {
  const ctx = useContext(DemoContext);
  if (!ctx) {
    throw new Error('useDemo must be used within a DemoProvider');
  }
  return ctx;
}

/**
 * Safe version that returns isDemoMode=false when used outside DemoProvider.
 * Useful for components that may render before providers are mounted.
 */
export function useDemoSafe(): DemoContextValue {
  const ctx = useContext(DemoContext);
  return ctx ?? {
    isDemoMode: false,
    isAutoDemo: false,
    enableDemo: () => {},
    disableDemo: () => {},
    toggleDemo: () => {},
    dismissAutoDemoBanner: () => {},
    showAutoDemoBanner: false,
    demoData: DEMO_DATA,
  };
}
